import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { canDo } from '../utils/canDo';
import {
  FaCheckCircle, FaExclamationCircle,
  FaTimesCircle, FaBan, FaPlus, FaCalendarAlt,
  FaTrash, FaCheck, FaTimes, FaExchangeAlt
} from 'react-icons/fa';
import HandoverModal from './HandoverModal';

const LeaveUpdates = function () {
  var auth = useAuth().auth;

  var [leaveStartDate, setLeaveStartDate] = useState('');
  var [leaveDays, setLeaveDays] = useState(0);
  var [leaveTherapist, setLeaveTherapist] = useState('');
  var [resources, setResources] = useState([]);
  var [branches, setBranches] = useState([]);
  var [leaves, setLeaves] = useState([]);
  var [newBranch, setNewBranch] = useState(auth.branch || 'MedicalYogaTherapyCentre');
  var [leaveforwhom, setLeaveForWhom] = useState('Self');
  var [leaveHours, setLeaveHours] = useState(0);
  var [leaveTime, setLeaveTime] = useState('');
  var [leaveComments, setLeaveComments] = useState('');
  var [leaveType, setLeaveType] = useState('planned');
  var [showForm, setShowForm] = useState(false);
  var [statusFilter, setStatusFilter] = useState('all');

  // User picker for admin/owner/solution
  var [allStaff, setAllStaff] = useState([]);
  var [viewUser, setViewUser] = useState('');  // '' = self, '__ALL__' = all, else = specific username
  var [showUserPicker, setShowUserPicker] = useState(false);

  // Handover state
  var [handovers, setHandovers] = useState([]);
  var [handoverModalLeave, setHandoverModalLeave] = useState(null);  // leave object to manage handover for
  const canHandover = canDo(auth, 'leaves.handover');

  const canViewOthers = canDo(auth, 'leaves.view_all');
  const activeUser = viewUser || auth.username;

  function formatDate(dateStr) {
    if (!dateStr) return '—';
    var date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  function formatTime(dateStr) {
    if (!dateStr) return '';
    var date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  useEffect(function () {
    getBranches();
    getTherapists();
    if (leaveforwhom !== 'Other') {
      setLeaveTherapist(auth.username);
      setLeaveForWhom('Self');
    }
    getLeaves();
    fetchHandovers();
  }, []);

  useEffect(function () {
    getTherapists();
  }, [newBranch]);

  // Refetch leaves when viewUser changes
  useEffect(function () {
    getLeaves();
  }, [viewUser]);

  useEffect(function () {
    getLeaves();
  }, [leaveforwhom]);

  function approveLeave(id, approve) {
    var approved = approve ? 'approved' : 'cancelled';
    api
      .post(process.env.REACT_APP_URL + '/appointments/update/' + id, { approved: approved })
      .then(function () { getLeaves(); })
      .catch(function (error) { console.error('Error updating leave', error); });
  }

  function deleteLeave(id) {
    var ret = window.confirm('Are you sure you want to Delete this ?');
    if (!ret) return;
    api
      .delete(process.env.REACT_APP_URL + '/appointments/' + id)
      .then(function () { getLeaves(); })
      .catch(function (error) { alert('Error deleting leave'); });
  }

  function getBranches() {
    api.get(process.env.REACT_APP_URL + '/branches')
      .then(function (response) { setBranches(response.data); })
      .catch(function (error) { console.error('Error fetching branches:', error); });
  }

  function getTherapists() {
    api.get(process.env.REACT_APP_URL + '/users/staff/all')
      .then(function (response) {
        let fusers = [];
        response?.data.forEach(element => {
          if (!["parul", "shashi", "snsachdeva"].includes(element.username))
            fusers.push({ username: element.username, number: element.number, roles: element.roles, _id: element._id });
        });
        setResources(fusers);
        setAllStaff((response.data || []).filter(u => u.username));
      })
      .catch(function (error) { console.error('Error fetching staff', error); });
  }

  function getLeaves() {
    // Determine which user's leaves to fetch
    var empid = '';
    if (canViewOthers && viewUser === '__ALL__') {
      empid = ''; // fetch all
    } else if (canViewOthers && viewUser) {
      empid = viewUser; // fetch specific user
    } else if (leaveforwhom === 'Other') {
      empid = ''; // admin creating for other — show all
    } else {
      empid = auth.username; // self
    }
    api.get(process.env.REACT_APP_URL + '/appointments/type/Leave/' + empid)
      .then(function (response) { setLeaves(response.data); })
      .catch(function (error) { console.error('Error fetching Leaves:', error); });
  }

  var fetchHandovers = useCallback(function () {
    if (!canHandover) return;
    api.get(process.env.REACT_APP_URL + '/handovers')
      .then(function (res) { setHandovers(res.data || []); })
      .catch(function () { /* ignore */ });
  }, [canHandover]);

  function getHandoverForLeave(row) {
    if (!row || !handovers.length) return null;
    return handovers.find(function (h) {
      return h.therapistOnLeave === row.username &&
        new Date(h.startDate).toDateString() === new Date(row.start).toDateString();
    });
  }

  function getSortedLeaves(bills) {
    if (!bills) return [];
    var sorted = bills.slice().sort(function (a, b) {
      return new Date(b.start) - new Date(a.start);
    });
    if (statusFilter === 'all') return sorted;
    return sorted.filter(function (l) { return l.approved === statusFilter; });
  }

  function parseTime12toDate(dateStr, timeStr) {
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    const date = new Date(dateStr);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  function handleCreateLeave() {
    var u = leaveTherapist;
    if (!leaveTherapist || (auth.roles && auth.roles.indexOf('therapist') !== -1)) {
      u = auth.username;
      setLeaveTherapist(auth.username);
    }
    if (!leaveStartDate || (leaveDays < 1 && leaveHours < 1)) {
      alert('Please fill in all fields correctly.');
      return;
    }
    var start = new Date(leaveStartDate);
    var leaveEvents = [];
    if (leaveDays > 0) {
      for (var i = 0; i < leaveDays; i++) {
        var leaveDate = new Date(start);
        leaveDate.setDate(start.getDate() + i);
        var leaveStart = new Date(leaveDate);
        leaveStart.setHours(6, 0, 0, 0);
        var leaveEnd = new Date(leaveDate);
        leaveEnd.setHours(20, 0, 0, 0);
        leaveEvents.push({
          start: leaveStart, end: leaveEnd, resourceId: u,
          title: leaveComments || u + '-Leave', username: u,
          name: 'Leave', type: 'Leave', patientCard: null,
          date: new Date(), branch: newBranch || auth.branch || 'MedicalYogaTherapyCentre',
          approved: 'applied',
          leaveType: leaveType,
        });
      }
    }
    if (leaveHours > 0) {
      var leaveDate2 = new Date(start);
      leaveDate2.setDate(start.getDate() + (leaveDays || 0));
      const leaveStart2 = parseTime12toDate(leaveDate2, leaveTime);
      const leaveEnd2 = new Date(leaveStart2);
      leaveEnd2.setHours(leaveStart2.getHours() + leaveHours);
      leaveEvents.push({
        start: leaveStart2, end: leaveEnd2, resourceId: u,
        title: leaveComments || u + '-Leave', username: u,
        name: 'Leave', type: 'Leave', patientCard: null,
        date: new Date(), branch: newBranch || auth.branch || 'MedicalYogaTherapyCentre',
        approved: 'applied',
      });
    }
    api.post(process.env.REACT_APP_URL + '/appointments/add/multiple', leaveEvents)
      .then(function () {
        alert('Leave(s) Created');
        setShowForm(false);
        getLeaves();
      })
      .catch(function (error) { console.error('Error saving leave event:', error); });
  }

  function handleChange(value) {
    setLeaveForWhom(value);
  }

  function getStatusInfo(status, row) {
    switch (status) {
      case 'applied': return { label: 'Applied', bg: '#dbeafe', color: '#1d4ed8', icon: <FaExclamationCircle size={11} /> };
      case 'approved': return { label: 'Approved', bg: '#dcfce7', color: '#16a34a', icon: <FaCheckCircle size={11} /> };
      case 'cancelled': return { label: 'Cancelled', bg: '#fee2e2', color: '#dc2626', icon: <FaBan size={11} /> };
      case 'auto-attendance': return { label: row?.attendanceType ? 'Auto: ' + row.attendanceType : 'Auto', bg: '#fff7ed', color: '#c2410c', icon: <FaExclamationCircle size={11} /> };
      default: return { label: 'Pending', bg: '#f3f4f6', color: '#6b7280', icon: <FaExclamationCircle size={11} /> };
    }
  }

  const isAdmin = canDo(auth, 'leaves.approve');

  // Stats
  const totalLeaves = leaves.length;
  const appliedCount = leaves.filter(l => l.approved === 'applied').length;
  const approvedCount = leaves.filter(l => l.approved === 'approved').length;
  const autoCount = leaves.filter(l => l.approved === 'auto-attendance').length;

  const viewLabel = viewUser === '__ALL__' ? 'All Staff' : viewUser ? viewUser : auth.username;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '16px 12px' }} onClick={() => showUserPicker && setShowUserPicker(false)}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            {viewUser === '__ALL__' ? 'All Leaves' : viewUser ? viewUser + "'s Leaves" : 'My Leaves'}
          </h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {viewUser === '__ALL__' ? 'Showing all staff' : (viewUser || auth.username)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* User picker — admin/owner/solution/supervisor */}
          {canViewOthers && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setShowUserPicker(v => !v); }}
                style={{
                  padding: '7px 16px', borderRadius: 8,
                  border: '1.5px solid ' + (showUserPicker ? '#3b82f6' : '#cbd5e1'),
                  background: showUserPicker ? '#eff6ff' : '#f8fafc',
                  color: showUserPicker ? '#1d4ed8' : '#334155',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                👥 {viewLabel === auth.username ? 'My Leaves' : viewLabel} ▾
              </button>

              {showUserPicker && (
                <div onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: '110%', right: 0, zIndex: 999,
                    background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    border: '1px solid #e2e8f0', minWidth: 240, maxHeight: 380, overflowY: 'auto',
                    padding: '8px 0',
                  }}
                >
                  {/* Quick actions */}
                  <div style={{ display: 'flex', gap: 6, padding: '6px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                    <button onClick={() => { setViewUser('__ALL__'); setShowUserPicker(false); }}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #bfdbfe', background: viewUser === '__ALL__' ? '#dbeafe' : '#eff6ff', color: '#1d4ed8', fontWeight: 700, cursor: 'pointer' }}>
                      All Staff
                    </button>
                    <button onClick={() => { setViewUser(''); setShowUserPicker(false); }}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: !viewUser ? '#dcfce7' : '#f0fdf4', color: '#16a34a', fontWeight: 700, cursor: 'pointer' }}>
                      Only Me
                    </button>
                    <button onClick={() => setShowUserPicker(false)}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}>
                      Done
                    </button>
                  </div>

                  {/* Staff list */}
                  {allStaff
                    .filter(u => u.status !== 'inactive' && u.status !== 'terminated')
                    .sort((a, b) => a.username.localeCompare(b.username))
                    .map(u => (
                      <div key={u.username}
                        onClick={() => { setViewUser(u.username === auth.username ? '' : u.username); setShowUserPicker(false); }}
                        style={{
                          padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 8,
                          background: (u.username === activeUser && viewUser !== '__ALL__') ? '#eff6ff' : 'transparent',
                          color: (u.username === activeUser && viewUser !== '__ALL__') ? '#1d4ed8' : '#334155',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.background = (u.username === activeUser && viewUser !== '__ALL__') ? '#eff6ff' : 'transparent'}
                      >
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: (u.username === activeUser && viewUser !== '__ALL__') ? '#2563eb' : '#cbd5e1',
                          flexShrink: 0,
                        }} />
                        {u.username}
                        {u.username === auth.username && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' }}>(you)</span>}
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          <button onClick={() => setShowForm(!showForm)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10,
            border: 'none', background: showForm ? '#f1f5f9' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            color: showForm ? '#475569' : '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            boxShadow: showForm ? 'none' : '0 4px 14px rgba(37,99,235,0.25)',
          }}>
            {showForm ? <><FaTimes size={12} /> Cancel</> : <><FaPlus size={12} /> Apply Leave</>}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div onClick={() => setStatusFilter('all')} style={{ background: statusFilter === 'all' ? 'linear-gradient(135deg, #1e40af, #2563eb)' : '#f8fafc', color: statusFilter === 'all' ? '#fff' : '#334155', borderRadius: 12, padding: '12px 14px', textAlign: 'center', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s' }}>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{totalLeaves}</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, fontWeight: 600 }}>Total</div>
        </div>
        <div onClick={() => setStatusFilter('applied')} style={{ background: statusFilter === 'applied' ? 'linear-gradient(135deg, #1d4ed8, #3b82f6)' : '#f8fafc', color: statusFilter === 'applied' ? '#fff' : '#334155', borderRadius: 12, padding: '12px 14px', textAlign: 'center', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s' }}>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{appliedCount}</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, fontWeight: 600 }}>Applied</div>
        </div>
        <div onClick={() => setStatusFilter('approved')} style={{ background: statusFilter === 'approved' ? 'linear-gradient(135deg, #16a34a, #22c55e)' : '#f8fafc', color: statusFilter === 'approved' ? '#fff' : '#334155', borderRadius: 12, padding: '12px 14px', textAlign: 'center', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s' }}>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{approvedCount}</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, fontWeight: 600 }}>Approved</div>
        </div>
        <div onClick={() => setStatusFilter('auto-attendance')} style={{ background: statusFilter === 'auto-attendance' ? 'linear-gradient(135deg, #c2410c, #ea580c)' : '#f8fafc', color: statusFilter === 'auto-attendance' ? '#fff' : '#334155', borderRadius: 12, padding: '12px 14px', textAlign: 'center', cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s' }}>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{autoCount}</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, fontWeight: 600 }}>Auto</div>
        </div>
      </div>

      {/* Apply Leave Form */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaCalendarAlt style={{ color: '#2563eb' }} /> Apply for Leave
          </h3>

          {isAdmin && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Apply for:</label>
                <select value={leaveforwhom} onChange={e => handleChange(e.target.value)}
                  style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, color: '#334155', background: '#f8fafc' }}>
                  <option value="Self">Self</option>
                  <option value="Other">Other Staff</option>
                </select>
              </div>
              {leaveforwhom === 'Other' && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select value={newBranch} onChange={e => setNewBranch(e.target.value)}
                    style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }}>
                    <option value="selectB">Select Branch</option>
                    {branches.filter(b => b.isActive !== false && b.status !== 'inactive').map(e => (
                      <option key={e.name} value={e.name}>{e.name}</option>
                    ))}
                  </select>
                  {newBranch !== 'selectB' && (
                    <select onChange={e => setLeaveTherapist(e.target.value)}
                      style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }}>
                      <option value="">Select User</option>
                      {resources.map(r => (
                        <option key={r.username} value={r.username}>{r.username}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Start Date</label>
              <input type="date" onChange={e => setLeaveStartDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Days</label>
              <input type="number" min={0} step={1} onChange={e => setLeaveDays(parseInt(e.target.value, 10) || 0)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Time</label>
              <select value={leaveTime || ''} onChange={e => setLeaveTime(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }}>
                <option value="">Select Time</option>
                {['08:00 AM','09:00 AM','10:00 AM','11:00 AM','12:00 PM','01:00 PM','02:00 PM','03:00 PM','04:00 PM','05:00 PM','06:00 PM','07:00 PM','08:00 PM','09:00 PM'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Hours</label>
              <input type="number" min={0} step={0.5} onChange={e => setLeaveHours(parseFloat(e.target.value) || 0)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc' }} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Leave Type</label>
            <select value={leaveType} onChange={e => setLeaveType(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, color: '#334155', background: '#f8fafc' }}>
              <option value="planned">Planned</option>
              <option value="unplanned">Unplanned</option>
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Reason / Comments</label>
            <textarea rows={2} placeholder="Enter reason for leave..."
              onChange={e => setLeaveComments(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc', resize: 'vertical' }} />
          </div>

          <button onClick={handleCreateLeave} style={{
            padding: '10px 28px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff',
            fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.25)',
          }}>
            Submit Leave Request
          </button>
        </div>
      )}

      {/* Leave Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {getSortedLeaves(leaves).length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>No leaves found.</div>
        )}
        {getSortedLeaves(leaves).map(function (row) {
          var si = getStatusInfo(row.approved, row);
          var startD = new Date(row.start);
          var endD = new Date(row.end);
          var isSameDay = startD.toDateString() === endD.toDateString();

          return (
            <div key={row._id} style={{
              background: '#fff', borderRadius: 12, padding: '14px 18px',
              borderLeft: '4px solid ' + si.color, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                {/* Status badge */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: si.bg, color: si.color, fontSize: 11, fontWeight: 700,
                    padding: '3px 10px', borderRadius: 6,
                  }}>
                    {si.icon} {si.label}
                  </span>
                  {/* Username badge — always show when viewing all or another user */}
                  {(viewUser === '__ALL__' || (viewUser && row.username !== viewUser)) && row.username && (
                    <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#334155' }}>
                      {row.username}
                    </span>
                  )}
                </div>
                {/* Date range */}
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                  {formatDate(row.start)}
                  {!isSameDay && <span style={{ color: '#94a3b8', fontWeight: 400 }}> — {formatDate(row.end)}</span>}
                </div>
                {/* Time */}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {formatTime(row.start)} — {formatTime(row.end)}
                </div>
                {/* Reason/title */}
                {row.title && (
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>{row.title}</div>
                )}
                {/* Auto-attendance details */}
                {row.autoGenerated && row.attendanceSlots != null && (
                  <div style={{ fontSize: 11, color: '#c2410c', marginTop: 4, fontWeight: 600 }}>
                    Attendance: {row.attendanceSlots}/{row.attendanceTotal} slots present
                  </div>
                )}
                {/* Leave type + Handover badge */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                  {row.leaveType && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                      background: row.leaveType === 'unplanned' ? '#fef3c7' : '#e0e7ff',
                      color: row.leaveType === 'unplanned' ? '#92400e' : '#3730a3',
                    }}>
                      {row.leaveType === 'unplanned' ? 'Unplanned' : 'Planned'}
                    </span>
                  )}
                  {canHandover && (function () {
                    var ho = getHandoverForLeave(row);
                    if (ho) {
                      return (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                          background: ho.status === 'active' ? '#dcfce7' : ho.status === 'reverted' ? '#f1f5f9' : '#fef9c3',
                          color: ho.status === 'active' ? '#16a34a' : ho.status === 'reverted' ? '#64748b' : '#a16207',
                        }}>
                          Handover: {ho.status.charAt(0).toUpperCase() + ho.status.slice(1)}
                        </span>
                      );
                    }
                    return (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: '#f1f5f9', color: '#94a3b8' }}>
                        No Handover
                      </span>
                    );
                  })()}
                </div>
              </div>
              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                {canHandover && row.approved !== 'cancelled' && (
                  <button onClick={() => setHandoverModalLeave(row)} title="Manage Handover"
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid #c7d2fe',
                      background: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                    }}>
                    <FaExchangeAlt size={11} /> Handover
                  </button>
                )}
                {isAdmin && (
                  <>
                    {row.approved !== 'approved' && row.approved !== 'auto-attendance' && (
                      <button onClick={() => approveLeave(row._id, 1)} title="Approve"
                        style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                        <FaCheck />
                      </button>
                    )}
                    {row.approved !== 'cancelled' && row.approved !== 'auto-attendance' && (
                      <button onClick={() => approveLeave(row._id, 0)} title="Cancel"
                        style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                        <FaBan />
                      </button>
                    )}
                    <button onClick={() => deleteLeave(row._id)} title="Delete"
                      style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                      <FaTrash />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Handover Modal */}
      {handoverModalLeave && (
        <HandoverModal
          leave={handoverModalLeave}
          allStaff={allStaff}
          existingHandover={getHandoverForLeave(handoverModalLeave)}
          onClose={() => { setHandoverModalLeave(null); fetchHandovers(); }}
        />
      )}
    </div>
  );
};

export default LeaveUpdates;
